import React from "react";

// Crypto portal runs on a `crypto.*` host; every other host is the NSE portal.
const IS_CRYPTO_PORTAL = typeof window !== "undefined" && /^crypto\./i.test(window.location.hostname || "");
// A crypto (Delta Exchange) broker — provider 'delta', or named like one.
const isDeltaBroker = (b) => !!(b && (b.provider === "delta" || /delta/i.test(b.name || "")));

// The user's brokers for the current portal (each carrying a `connected` flag) —
// for the per-strategy broker picker on Live deploy. Self-contained (reads the JWT
// straight from localStorage) so it works from both App.js and the pages. The NSE
// portal shows only NSE brokers and the crypto portal only Delta brokers, so a
// strategy can never be deployed on a broker from the wrong portal. Connected
// brokers sort first.
export function useBrokers() {
  const [brokers, setBrokers] = React.useState([]);
  React.useEffect(() => {
    const token = localStorage.getItem("token");
    fetch((process.env.REACT_APP_API_URL || "/api") + "/brokers", {
      headers: token ? { Authorization: "Bearer " + token } : {},
    })
      .then((r) => r.json())
      .then((d) => Array.isArray(d) && setBrokers(
        [...d]
          .filter((b) => (IS_CRYPTO_PORTAL ? isDeltaBroker(b) : !isDeltaBroker(b)))
          .sort((a, b) => (b.connected ? 1 : 0) - (a.connected ? 1 : 0))
      ))
      .catch(() => {});
  }, []);
  return brokers;
}

// Currency-aware money formatter — crypto (Delta) margin comes back in USD, NSE in ₹.
const money = (n, cur) => (cur === "USD" ? "$" : "₹") + Math.round(Number(n) || 0).toLocaleString(cur === "USD" ? "en-US" : "en-IN");

// "Go Live" modal: pick the broker a strategy trades through, see whether that
// broker has enough margin for this strategy, and accept the Terms & Conditions
// before any real-money deploy. `onConfirm(brokerId)` fires only once a broker is
// selected, the margin is sufficient (or couldn't be checked), and T&C is
// checked. A disconnected broker can still be pinned — the strategy just won't
// trade until it reconnects.
export function BrokerPickModal({ brokers, strategyId, onConfirm, onClose }) {
  const [sel, setSel] = React.useState(null);   // selected broker id
  const [tnc, setTnc] = React.useState(false);  // T&C accepted
  const [margin, setMargin] = React.useState(null); // margin-check result
  const [checking, setChecking] = React.useState(false);

  // On broker select, check whether it has enough margin for this strategy.
  React.useEffect(() => {
    if (!sel || !strategyId) { setMargin(null); return; }
    let cancelled = false;
    setChecking(true); setMargin(null);
    const token = localStorage.getItem("token");
    fetch((process.env.REACT_APP_API_URL || "/api") + "/strategies/" + strategyId + "/margin-check", {
      method: "POST",
      headers: { "Content-Type": "application/json", ...(token ? { Authorization: "Bearer " + token } : {}) },
      body: JSON.stringify({ brokerId: sel }),
    })
      .then((r) => r.json())
      .then((d) => { if (!cancelled) setMargin(d); })
      .catch(() => { if (!cancelled) setMargin({ ok: true, skipped: true, reason: "check failed" }); })
      .finally(() => { if (!cancelled) setChecking(false); });
    return () => { cancelled = true; };
  }, [sel, strategyId]);

  const insufficient = !!margin && margin.ok === false;
  const canGo = !!sel && tnc && !checking && !insufficient;
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1100 }}>
      <div style={{ background: "#fff", borderRadius: 16, padding: 28, width: 420, maxWidth: "92vw", boxShadow: "0 20px 60px rgba(0,0,0,0.15)" }}>
        <h2 style={{ fontWeight: 800, marginBottom: 4, fontSize: 19 }}>🚀 Go Live</h2>
        <div style={{ fontSize: 12.5, color: "#6b7280", marginBottom: 16 }}>Broker chuno jis pe ye strategy trade karegi — saare orders (entry, SL, exit) isi pe jayenge.</div>

        {brokers.length === 0 && (
          <div style={{ padding: "14px 16px", borderRadius: 10, background: "#fff7ed", color: "#9a3412", fontSize: 13, fontWeight: 600, marginBottom: 16 }}>
            Koi broker nahi mila. Pehle Broker page se ek broker add &amp; connect karo.
          </div>
        )}

        {brokers.map((b) => {
          const active = sel === b._id;
          return (
            <button key={b._id} onClick={() => setSel(b._id)}
              style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 12, width: "100%", maxWidth: "100%", textAlign: "left", padding: "12px 14px", marginBottom: 10, border: `2px solid ${active ? "#2563eb" : "#e8eaf0"}`, borderRadius: 10, background: active ? "#eff6ff" : "#fafafa", cursor: "pointer" }}>
              <div style={{ width: 18, height: 18, borderRadius: "50%", border: `2px solid ${active ? "#2563eb" : "#cbd5e1"}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                {active && <div style={{ width: 9, height: 9, borderRadius: "50%", background: "#2563eb" }} />}
              </div>
              <div style={{ width: 34, height: 34, borderRadius: 8, background: "#fff", border: "1px solid #e8eaf0", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, color: /upstox/i.test(b.name) ? "#5b21b6" : "#ef4444" }}>{/upstox/i.test(b.name) ? "U" : "Z"}</div>
              <div style={{ flex: 1, minWidth: 0 }}><div style={{ fontWeight: 700, fontSize: 14, color: "#1f2937", overflowWrap: "anywhere" }}>{b.name}</div><div style={{ fontSize: 12, color: "#2563eb", overflowWrap: "anywhere" }}>{b.clientId}</div></div>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 9px", borderRadius: 20, fontSize: 11, fontWeight: 700, background: b.connected ? "#d1fae5" : "#fee2e2", color: b.connected ? "#065f46" : "#991b1b" }}>
                <span style={{ width: 5, height: 5, borderRadius: "50%", background: b.connected ? "#10b981" : "#ef4444" }} />
                {b.connected ? "Connected" : "Not connected"}
              </span>
            </button>
          );
        })}

        {sel && checking && (
          <div style={{ padding: "10px 14px", borderRadius: 10, background: "#f1f5f9", color: "#475569", fontSize: 12.5, fontWeight: 600, margin: "2px 0 14px" }}>⏳ Margin check ho raha hai…</div>
        )}
        {sel && !checking && margin && margin.ok === false && (
          <div style={{ padding: "12px 14px", borderRadius: 10, background: "#fef2f2", border: "1px solid #fecaca", margin: "2px 0 14px" }}>
            <div style={{ fontSize: 13, fontWeight: 800, color: "#991b1b", marginBottom: 4 }}>❌ Insufficient balance</div>
            <div style={{ fontSize: 12, color: "#7f1d1d", lineHeight: 1.6 }}>
              Required: <b>{money(margin.required, margin.currency)}</b> &nbsp;·&nbsp; Available: <b>{money(margin.available, margin.currency)}</b><br />
              Shortfall: <b>{money(margin.shortfall, margin.currency)}</b>. Lots kam karo ya funds add karke dobara try karo.
            </div>
          </div>
        )}
        {sel && !checking && margin && margin.ok && !margin.skipped && (
          <div style={{ padding: "10px 14px", borderRadius: 10, background: "#ecfdf5", border: "1px solid #a7f3d0", color: "#065f46", fontSize: 12.5, margin: "2px 0 14px" }}>
            ✅ Margin OK — required <b>{money(margin.required, margin.currency)}</b>, available <b>{money(margin.available, margin.currency)}</b>{margin.estimated ? " (est.)" : ""}
          </div>
        )}
        {sel && !checking && margin && margin.skipped && (
          <div style={{ padding: "10px 14px", borderRadius: 10, background: "#fffbeb", border: "1px solid #fde68a", color: "#92400e", fontSize: 12, margin: "2px 0 14px", lineHeight: 1.5 }}>
            ⚠️ Margin verify nahi ho saka ({margin.reason || "unknown"}) — deploy allowed, par funds khud check kar lena.
          </div>
        )}

        <label style={{ display: "flex", alignItems: "flex-start", gap: 9, margin: "6px 0 16px", cursor: "pointer", fontSize: 12.5, color: "#374151", lineHeight: 1.5 }}>
          <input type="checkbox" checked={tnc} onChange={(e) => setTnc(e.target.checked)} style={{ width: 16, height: 16, marginTop: 1, flexShrink: 0 }} />
          <span>Main <a href="/terms" target="_blank" rel="noreferrer" style={{ color: "#2563eb", fontWeight: 700 }}>Terms &amp; Conditions</a> accept karta hoon — live trading mein asli paisा risk par hai.</span>
        </label>

        <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
          <button onClick={onClose} style={{ flex: 1, padding: 12, border: "1px solid #e8eaf0", borderRadius: 10, background: "#fff", cursor: "pointer", fontSize: 14, fontWeight: 600 }}>Cancel</button>
          <button onClick={() => canGo && onConfirm(sel)} disabled={!canGo}
            style={{ flex: 1, padding: 12, border: "none", borderRadius: 10, background: canGo ? "linear-gradient(135deg,#22c55e,#16a34a)" : "#cbd5e1", color: "#fff", cursor: canGo ? "pointer" : "default", fontSize: 14, fontWeight: 800 }}>
            🚀 Go Live
          </button>
        </div>
      </div>
    </div>
  );
}
